// src/pages/AllFlash.js
import React, { useState, useEffect, useContext, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Grid,
} from "@mui/material";
import skip from "./assets/skip.png";
import forgot from "./assets/forgot.png";
import partially from "./assets/partially.png";
import efforts from "./assets/efforts.png";
import convenient from "./assets/convenient.png";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import { ThemeContext } from "../context/ThemeContext";
import katex from "katex";
import "katex/dist/katex.min.css";
import sanitizeHtml from "sanitize-html";
import parse from "html-react-parser";

const listStyles = `
  .rendered-content ul,
  .rendered-content ol {
    padding-left: 20px;
    margin: 6px 0;
    list-style: disc;
  }

  .rendered-content ol {
    list-style: decimal;
  }

  .rendered-content li {
    display: list-item;
    margin-bottom: 4px;
  }
`;

// --- Styled Components with Card Animation ---
const StyledCard = styled(Card)(({ theme, isExiting }) => ({
  maxWidth: 900,
  minHeight: "auto",
  margin: "auto",
  borderRadius: 24,
  boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
  background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
  padding: theme.spacing(3),
  position: "relative",
  overflow: "hidden",
  animation: isExiting ? "slideOutLeft 0.5s ease-out" : "slideInRight 0.5s ease-out",
  "@keyframes slideInRight": {
    from: { transform: "translateX(100%)", opacity: 0 },
    to: { transform: "translateX(0)", opacity: 1 },
  },
  "@keyframes slideOutLeft": {
    from: { transform: "translateX(0)", opacity: 1 },
    to: { transform: "translateX(-100%)", opacity: 0 },
  },
}));

const StyledCardContent = styled(CardContent)(({ theme, mode, customColors }) => ({
  backgroundColor: mode === "dark" ? "#1a1a2e" : mode === "custom" ? customColors.bg : "#ffffff",
  padding: theme.spacing(4),
  minHeight: "auto",
  borderRadius: 16,
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  color: mode === "dark" ? "#ffffff" : mode === "custom" ? customColors.text : "#000000",
  boxShadow: "inset 0 2px 10px rgba(0,0,0,0.1)",
}));

const CardHeader = styled(Box)(() => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 16,
  padding: "12px 0",
}));

const QuestionBadge = styled(Box)(({ theme }) => ({
  backgroundColor: "rgba(255,255,255,0.2)",
  color: "#fff",
  padding: "8px 20px",
  borderRadius: 20,
  fontWeight: "bold",
  fontSize: "0.9rem",
  backdropFilter: "blur(10px)",
}));

const ImageButton = styled(IconButton)(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 4,
  transition: "transform 0.2s",
  "&:hover": {
    transform: "scale(1.1)",
  },
}));

const Image = styled("img")(() => ({
  width: "50px",
  height: "50px",
  filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
}));

const ShowAnswerButton = styled(Button)(() => ({
  background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
  color: "#fff",
  padding: "14px 40px",
  borderRadius: 30,
  fontSize: "1.1rem",
  fontWeight: "bold",
  textTransform: "none",
  boxShadow: "0 8px 20px rgba(102,126,234,0.4)",
  transition: "all 0.3s",
  "&:hover": {
    background: "linear-gradient(135deg, #1e8bafff 0%, #1e3a8a 100%)",
    transform: "translateY(-2px)",
    boxShadow: "0 12px 28px rgba(102,126,234,0.5)",
  },
}));

const OptionButton = styled(Button)(({ theme, mode, customColors }) => ({
  textAlign: "left",
  justifyContent: "flex-start",
  padding: "16px 20px",
  borderRadius: 12,
  border: "2px solid #e0e0e0",
  backgroundColor: mode === "custom" ? customColors.background : "#f8f9fa",
  color: mode === "custom" ? customColors.text : "#333",
  textTransform: "none", // ← Add this line
  transition: "all 0.3s",
  "&:hover": {
    backgroundColor: mode === "custom" ? customColors.background : "#e3f2fd",
    borderColor: "#1e8bafff",
    transform: "translateX(4px)",
  },
}));

// --- Utils ---
const toApiFlashcardType = (t) => {
  if (!t) return "";
  const x = t.toLowerCase();
  if (x === "tf" || x === "truefalse") return "TRUEFALSE";
  if (x === "mcq") return "MCQ";
  if (x === "fib") return "FIB";
  if (x === "sub") return "SUB";
  return x.toUpperCase();
};

const sanitizeRichText = (html) => {
  if (typeof html !== "string") return "";
  return sanitizeHtml(html, {
    allowedTags: ["b", "i", "em", "strong", "u", "p", "br", "span", "div", "a"],
    allowedAttributes: { a: ["href", "target"], span: ["style"] },
    allowedSchemes: ["http", "https"],
  });
};

const renderContent = (html) => {
  if (!html || typeof html !== "string") return null;
  let cleanedHtml = html
    .replace(/(\$\$[^$]*\$\$|\$[^$]*\$)\s*hi/gi, "$1")
    .replace(/\s*hi\s*$/gi, "")
    .replace(/\bhi\b/gi, "")
    .trim();
  return parse(cleanedHtml, {
    replace: (domNode) => {
      if (domNode.type === "tag" && domNode.name === "span" && domNode.attribs?.class?.includes("ql-formula")) {
        const dataValue = domNode.attribs["data-value"] || "";
        try {
          const rendered = katex.renderToString(dataValue, { throwOnError: false, displayMode: false });
          return <span dangerouslySetInnerHTML={{ __html: rendered }} />;
        } catch (e) {
          return <span>[Formula Error]</span>;
        }
      }
      if (domNode.type === "text") {
        const text = domNode.data;
        if (!text.includes("$")) return text;
        const parts = text.split(/(\$\$.+?\$\$|\$.+?\$)/g);
        const children = [];
        parts.forEach((part, index) => {
          if (part.startsWith("$$") && part.endsWith("$$")) {
            const latex = part.slice(2, -2).trim();
            try {
              const rendered = katex.renderToString(latex, { displayMode: true, throwOnError: false });
              children.push(<div key={`math-block-${index}`} dangerouslySetInnerHTML={{ __html: rendered }} />);
            } catch (e) {
              children.push(<div key={`math-error-${index}`}>[Math Error]</div>);
            }
          } else if (part.startsWith("$") && part.endsWith("$")) {
            const latex = part.slice(1, -1).trim();
            try {
              const rendered = katex.renderToString(latex, { displayMode: false, throwOnError: false });
              children.push(<span key={`math-inline-${index}`} dangerouslySetInnerHTML={{ __html: rendered }} />);
            } catch (e) {
              children.push(<span key={`math-error-${index}`}>[Math Error]</span>);
            }
          } else {
            const cleanedPart = part.trim();
            if (cleanedPart && cleanedPart.toLowerCase() !== "hi") {
              children.push(cleanedPart);
            }
          }
        });
        return <>{children}</>;
      }
      return undefined;
    },
  });
};

const stripHtmlTags = (html) => {
  if (typeof html !== "string") return "";
  const latexPlaceholder = "___LATEX___";
  const latexExpressions = [];
  let index = 0;
  const latexPreserved = html.replace(/(\$\$.+?\$\$|\$.+?\$)/g, (match) => {
    latexExpressions[index] = match;
    return `${latexPlaceholder}${index++}${latexPlaceholder}`;
  });
  const formulaPreserved = latexPreserved.replace(/<[^>]*ql-formula[^>]*data-value="([^"]*)"[^>]*>/g, (match, value) => {
    latexExpressions[index] = `$ ${value} $`;
    return `${latexPlaceholder}${index++}${latexPlaceholder}`;
  });
  const cleaned = sanitizeHtml(formulaPreserved, { allowedTags: [], allowedAttributes: {} });
  let restored = cleaned;
  latexExpressions.forEach((latex, i) => {
    restored = restored.replace(`${latexPlaceholder}${i}${latexPlaceholder}`, latex);
  });
  return restored.replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/\s*hi\s*$/gi, "").trim();
};

const getImageSrc = (fileUrl, baseUrl) => {
  if (fileUrl.startsWith('http')) return fileUrl;
  return `${baseUrl}${fileUrl.startsWith('/') ? '' : '/'}${fileUrl}`;
};

// --- Main Component ---
const AllFlash = () => {
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [nextReviewAt, setNextReviewAt] = useState(null);
  const [countdown, setCountdown] = useState("");
  const [revealedLabelIndices, setRevealedLabelIndices] = useState([]);
  const [slideDirection, setSlideDirection] = useState("fade");
  const [srSnack, setSrSnack] = useState({ open: false, message: "", severity: "info" });

  const token = sessionStorage.getItem("authToken");
  const apiUrl = process.env.REACT_APP_BASE_URL;
  const { state } = useLocation();
  const navigate = useNavigate();
  const folder = state?.folder;
  const questionType = (state?.questionType || "ALL").toLowerCase();
  const spaceRepetitionEnabled = Boolean(state?.spaceRepetitionEnabled);
  const reviewMode = Boolean(state?.reviewMode);
  const { mode, customColors = {} } = useContext(ThemeContext);
const [isExiting, setIsExiting] = useState(false);
const userRole = sessionStorage.getItem('userRole');

useEffect(() => {
  window.scrollTo(0, 0);
}, [currentQuestionIndex]);

  // --- Fetch Questions ---
  useEffect(() => {
     // ✅ If questions were passed via navigation (from FlashPractice), use them directly
  if (state?.questions && Array.isArray(state.questions) && state.questions.length > 0) {
    const normalized = state.questions.map((q) => {
      const qtype = (q.question_type || q.questionType || "").toLowerCase();

      // Normalize answers
      let answers = [];
      if (qtype === "mcq") {
        answers = Array.isArray(q.answers)
          ? q.answers.map((a) => ({
              ...a,
              answer_text: String(a.answer_text ?? a.answer ?? a.text ?? ""),
            }))
          : [];
      } else if (qtype === "fib") {
        answers = Array.isArray(q.answers)
          ? q.answers.map((a) => ({
              answer_text: String(a.answer ?? a.answer_text ?? a.text ?? ""),
              is_correct: Boolean(a.is_correct),
              explanation: a.explanation || "",
            }))
          : [];
      } else if (qtype === "truefalse") {
        // answers can be null or "True"/"False" string
        const ans = q.answers;
        answers = [
          {
            answer_text: typeof ans === "string" ? ans : "True",
            is_correct: true,
          },
        ];
      } else if (qtype === "image") {
        return {
          id: q.id,
          statement: q.statement,
          file_url: q.file_url || null,
          labels: q.labels || [],
          canvas_width: q.canvas_width || null,
          canvas_height: q.canvas_height || null,
          questionType: "image",
          subfolder_id: q.subfolder_id || q.folder,
        };
      }

      return {
        id: q.id,
        statement: q.statement,
        answers,
        explanation: q.explanation || "",
        questionType: qtype === "truefalse" ? "tf" : qtype,
        subfolder_id: q.subfolder_id,
        file_url: q.image || q.file_url || null,
      };
    });

    setQuestions(normalized.sort(() => Math.random() - 0.5));
    setIsLoading(false);
    return; // ✅ Skip API fetch
  }
    const fetchQuestions = async () => {
      if (!token || !apiUrl) return;
      setIsLoading(true);
      setError(null);
      try {
        let url;
        if (reviewMode) {
          if (!state?.folderId) {
            setError("⚠️ No folder ID provided.");
            return;
          }
          url = `${apiUrl}/flashcards/review/due/?folder_id=${state.folderId}`;
        } else if (questionType === "all") {
          url = `${apiUrl}/questions/combined/${folder.id}/`;
        } else {
          const typeMap = { tf: "truefalse", mcq: "mcq", fib: "fib", sub: "sub", truefalse: "truefalse", image: userRole === 'admin' ? 'uploadimage' : 'uploaded_images' };

;
          url = `${apiUrl}/subfolder/${folder.id}/${typeMap[questionType] || questionType}/`;
        }
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        let normalized = Array.isArray(data) ? data.map((q) => {
          const flash = q.flashcard || q;
          const qtype = (flash.question_type || "").toLowerCase();
          let answers = [];
          if (qtype === "mcq") {
            if (Array.isArray(flash.answers)) {
              answers = flash.answers.map((a) => ({ ...a, answer_text: String(a.answer_text ?? a.answer ?? a.text ?? "") }));
            }
          } else if (Array.isArray(flash.answers)) {
            answers = flash.answers.map((a) => ({ answer_text: String(a.answer ?? a.answer_text ?? a.text ?? ""), is_correct: Boolean(a.is_correct), explanation: a.explanation || "" }));
          } else {
            let answerText = flash.answers ?? "";
            if (typeof answerText === "string") {
              answerText = String(answerText);
            } else if (typeof answerText === "object" && answerText !== null) {
              answerText = answerText.answer_text ?? answerText.answer ?? answerText.text ?? "";
            }
            answers = [{ answer_text: String(answerText), is_correct: true }];
          }
          if (qtype === "image") {
            return {
              id: flash.id || flash._id,
              statement: flash.statement,
              file_url: flash.file_url || flash.image || null,
              labels: flash.labels || [],
              canvas_width: flash.canvas_width || null,
              canvas_height: flash.canvas_height || null,
              questionType: qtype,
              subfolder_id: flash.folder,
              review_info: q.review_info || null,
            };
          }
          return {
            id: flash.id || flash._id,
            statement: flash.statement || flash.question,
            answers,
            explanation: flash.explanation || "",
            questionType: qtype === "truefalse" ? "tf" : qtype,
            subfolder_id: flash.subfolder_id,
            review_info: q.review_info || null,
            file_url: flash.image || flash.file_url || null,
          };
        }) : [];

        if (reviewMode) {
          const now = new Date();
          const dueCards = normalized.filter((q) => {
            if (!q.review_info) return false;
            if (q.review_info.status === "due") return true;
            const next = q.review_info.next_review ? new Date(q.review_info.next_review) : null;
            return next && !isNaN(next.getTime()) && next <= now;
          });
          const scheduledCards = normalized.filter((q) => {
            if (!q.review_info) return false;
            const next = q.review_info.next_review ? new Date(q.review_info.next_review) : null;
            return next && !isNaN(next.getTime()) && next > now;
          });
          if (dueCards.length) {
            normalized = dueCards;
          } else {
            if (scheduledCards.length) {
              const nextTimes = scheduledCards.map((q) => new Date(q.review_info.next_review)).filter((d) => !isNaN(d.getTime())).sort((a, b) => a - b);
              const nextAt = nextTimes[0];
              if (nextAt) {
                setNextReviewAt(nextAt);
                const msUntilNext = nextAt.getTime() - now.getTime();
                if (msUntilNext > 0) {
                  setTimeout(fetchQuestions, msUntilNext + 1000);
                }
              }
            } else {
              setError("🎉 No cards scheduled.");
            }
            normalized = [];
          }
        }
        setQuestions(normalized.sort(() => Math.random() - 0.5));
        if (!normalized.length) {
          if (!reviewMode || (reviewMode && !nextReviewAt)) {
            setError(reviewMode ? "🎉 No due cards. You're all caught up!" : "No questions found.");
          }
        }
      } catch (err) {
        setError(err.message || "Failed to load questions.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchQuestions();
  }, [folder, token, apiUrl, questionType, reviewMode, state]);

  useEffect(() => {
    if (!nextReviewAt) {
      setCountdown("");
      return;
    }
    const updateCountdown = () => {
      const now = new Date();
      const diff = nextReviewAt.getTime() - now.getTime();
      if (diff <= 0) {
        setCountdown("Due now!");
        return;
      }
      const mins = Math.floor(diff / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      setCountdown(`${mins}m ${secs}s`);
    };
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [nextReviewAt]);

  const postPracticeLog = async ({ flashcard_type, flashcard_id, feedback, subfolderId }) => {
    const res = await fetch(`${apiUrl}/practice-logs/`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ flashcard_type, flashcard_id: String(flashcard_id), feedback, meta: { subfolder_id: String(subfolderId) } }),
    });
    if (!res.ok) throw new Error(`Practice log failed: ${res.status}`);
    return res.json();
  };

  const postSpaceRepFeedback = async ({ flashcard_type, flashcard_id, feedback }) => {
    const res = await fetch(`${apiUrl}/feedbacks/`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ flashcard_type, flashcard_id: String(flashcard_id), feedback }),
    });
    if (!res.ok) throw new Error(`SRS feedback failed: ${res.status}`);
    return res.json();
  };

  const handleShowAnswer = () => setShowAnswer(true);

const afterFeedbackAdvance = () => {
  setIsExiting(true);
  setTimeout(() => {
    setShowAnswer(false);
    setRevealedLabelIndices([]);
    setCurrentQuestionIndex((prev) => (prev < questions.length - 1 ? prev + 1 : prev + 1));
    setIsExiting(false);
  }, 500); // Match animation duration
};


  const handleNext = async (feedback) => {
    const q = questions[currentQuestionIndex];
    if (!q) return;
    try {
      const flashcard_type = toApiFlashcardType(q.questionType);
      if (reviewMode || spaceRepetitionEnabled) {
        const json = await postSpaceRepFeedback({ flashcard_type, flashcard_id: q.id, feedback });
        const next = json?.review_schedule?.next_review;
        let msg = `Saved: ${feedback}.`;
        if (next) msg += ` Next review at ${new Date(next).toLocaleString()}.`;
        setSrSnack({ open: true, message: msg, severity: "success" });
      } else {
        await postPracticeLog({ flashcard_type, flashcard_id: q.id, feedback, subfolderId: q.subfolder_id });
        setSrSnack({ open: true, message: `Logged: ${feedback}`, severity: "success" });
      }
    } catch (err) {
      setSrSnack({ open: true, message: err.message, severity: "error" });
    } finally {
      afterFeedbackAdvance();
    }
  };

  const handleClose = () => {
    navigate("/flashpractice");
  };

  const renderAnswer = (question) => {
    if (question.questionType === "image") {
      return (
        <Box sx={{ mt: 2 }}>
        
          {question.labels.map((label, index) =>
            revealedLabelIndices.includes(index) && (
              <Box key={index}>
                <Typography variant="body1" sx={{ backgroundColor: "#e3f2fd", color: "#000", padding: "12px 16px", borderRadius: "8px", display: "block", margin: "8px 0", fontWeight: 500 }}>
                  {renderContent(`<strong>${label.id}:</strong> ${label.answer || ""}`)}
                </Typography>
                {label.explanation && (
                  <Typography variant="caption" sx={{ display: "block", color: "#666", mt: 1, pl: 2 }}>
                    {renderContent(label.explanation)}
                  </Typography>
                )}
              </Box>
            )
          )}
        </Box>
      );
    }

    let answerHtml;
    let explanationHtml = question.explanation || "No explanation available";

    if (question.questionType === "mcq") {
      const correctOption = question.answers.find((a) => a.is_correct);
      answerHtml = correctOption?.answer_text || "No correct answer";
      explanationHtml = correctOption?.explanation || question.explanation || "No explanation available for the correct option";
    } else if (question.questionType === "fib") {
      const correctAnswers = question.answers.filter((a) => a.is_correct).map((a) => a.answer_text).filter(Boolean);
      answerHtml = correctAnswers.length > 0 ? correctAnswers.join(" | ") : "No correct answer provided";
      const correctExpl = question.answers.find((a) => a.is_correct)?.explanation;
      explanationHtml = correctExpl || question.explanation || "No explanation available";
    } else if (question.questionType === "tf") {
      answerHtml = question.answers[0]?.answer_text || "True";
      explanationHtml = question.explanation || "No explanation available";
    } else {
      const correctAnswer = question.answers.find((a) => a.is_correct)?.answer_text || question.answers[0]?.answer_text || "No answer provided";
      answerHtml = correctAnswer;
      explanationHtml = question.answers[0]?.explanation || question.explanation || "No explanation available";
    }

    return (
      <Box sx={{ mt: 3, p: 3, backgroundColor: "rgba(30, 139, 175, 0.1)", borderRadius: 3, borderLeft: "4px solid #1e8bafff" }}>
        <Typography variant="h6" sx={{ color: mode === "dark" ? "#4fc3f7" : mode === "custom" ? customColors.text : "#1e8bafff", mb: 2, fontWeight: "bold" }}>
          ✓ Answer: {renderContent(answerHtml)}
        </Typography>
        <Typography variant="body2" sx={{ color: mode === "dark" ? "#ddd" : mode === "custom" ? customColors.text : "#555", lineHeight: 1.6 }}>
          💡  <span className="rendered-content">{renderContent(explanationHtml)}</span>
        </Typography>
      </Box>
    );
  };

  const renderOptions = (question) => {
    if (!question) return null;
    
    if (question.questionType === "image") {
      const allRevealed = revealedLabelIndices.length === question.labels.length;
      return (
        <Box sx={{ mt: 2 }}>
          <ImageQuestionDisplay question={question} revealedIndices={revealedLabelIndices} apiUrl={apiUrl} />
          {!allRevealed && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <ShowAnswerButton onClick={() => {
                const nextIndex = revealedLabelIndices.length;
                setRevealedLabelIndices((prev) => [...prev, nextIndex]);
              }}>
                Reveal Next ({revealedLabelIndices.length + 1}/{question.labels.length})
              </ShowAnswerButton>
            </Box>
          )}
        </Box>
      );
    }

    // if (question.questionType === "mcq" || question.questionType === "fib") {
    //   return (
    //     <Grid container spacing={2} sx={{ mt: 2 }}>
    //       {question.answers && question.answers.length > 0 ? (
    //         question.answers.map((opt, idx) => (
    //           <Grid item xs={12} sm={6} key={idx}>
    //             <OptionButton variant="outlined" fullWidth mode={mode} customColors={customColors}>
    //               <Box sx={{ width: "100%", textAlign: "left" }}>
    //                 {renderContent(opt.answer_text || "No text")}
    //               </Box>
    //             </OptionButton>
    //           </Grid>
    //         ))
    //       ) : (
    //         <Grid item xs={12}>
    //           <Typography>No options available</Typography>
    //         </Grid>
    //       )}
    //     </Grid>
    //   );
    // }

    if (question.questionType === "mcq" || question.questionType === "fib") {
  return null;
}
    if (question.questionType === "tf") {
      return (
        <Grid container spacing={2} sx={{ mt: 2 }}>
          {["True", "False"].map((opt, idx) => (
            <Grid item xs={12} sm={6} key={idx}>
              <OptionButton variant="outlined" fullWidth mode={mode} customColors={customColors}>
                {opt}
              </OptionButton>
            </Grid>
          ))}
        </Grid>
      );
    }

    return null;
  };

  const allDone = questions.length > 0 && currentQuestionIndex >= questions.length;

  const ImageQuestionDisplay = ({ question, revealedIndices = [], apiUrl }) => {
    const canvasRef = useRef(null);
    const [baseImageLoaded, setBaseImageLoaded] = useState(false);
    
    useEffect(() => {
      if (!canvasRef.current || !question.file_url || !question.labels || baseImageLoaded) return;
      
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const img = new window.Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        let canvasWidth, canvasHeight;
        
        if (question.canvas_width && question.canvas_height) {
          canvasWidth = question.canvas_width;
          canvasHeight = question.canvas_height;
        } else {
          const maxWidth = 800;
          const aspectRatio = img.width / img.height;
          canvasWidth = Math.min(maxWidth, img.width);
          canvasHeight = canvasWidth / aspectRatio;
        }
        
        canvas.width = canvasWidth;
        canvas.height = canvasHeight;
        ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight);
        
        const colors = ['rgba(255, 0, 0, 1)', 'rgba(0, 0, 255, 1)', 'rgba(0, 255, 0, 1)', 'rgba(255, 255, 0, 1)', 'rgba(255, 0, 255, 1)', 'rgba(0, 255, 255, 1)', 'rgba(255, 128, 0, 1)', 'rgba(128, 0, 255, 1)'];
        
        question.labels.forEach((label, index) => {
          ctx.fillStyle = colors[index % colors.length];
          ctx.fillRect(label.x, label.y, label.width, label.height);
          ctx.fillStyle = '#ffffff';
          ctx.font = 'bold 24px Arial';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(label.id, label.x + label.width / 2, label.y + label.height / 2 - 2);
        });
        
        setBaseImageLoaded(true);
      };
      
      img.onerror = () => console.error('Failed to load image:', question.file_url);
      img.src = question.file_url.startsWith('http') ? question.file_url : `${apiUrl}${question.file_url.startsWith('/') ? '' : '/'}${question.file_url}`;
    }, [question.file_url, question.labels, baseImageLoaded, question.canvas_width, question.canvas_height, apiUrl]);
    
    useEffect(() => {
      if (!baseImageLoaded || !canvasRef.current || revealedIndices.length === 0) return;
      
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      
      const colors = ['rgba(255, 0, 0, 1)', 'rgba(0, 0, 255, 1)', 'rgba(0, 255, 0, 1)', 'rgba(255, 255, 0, 1)', 'rgba(255, 0, 255, 1)', 'rgba(0, 255, 255, 1)', 'rgba(255, 128, 0, 1)', 'rgba(128, 0, 255, 1)'];
      
      question.labels.forEach((label, index) => {
        if (!revealedIndices.includes(index)) return;
        if (!label || typeof label.x !== 'number' || typeof label.y !== 'number' || typeof label.width !== 'number' || typeof label.height !== 'number' || !isFinite(label.x) || !isFinite(label.y) || !isFinite(label.width) || !isFinite(label.height) || label.width <= 0 || label.height <= 0) {
          return;
        }
        
        ctx.fillStyle = colors[index % colors.length];
        ctx.fillRect(label.x, label.y, label.width, label.height);
        
        const textX = label.x + label.width / 2;
        const textY = label.y + label.height / 2;
        const bgPadding = 6;
        
        ctx.font = 'bold 16px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const answerText = `${label.id}. ${label.answer || ''}`;
        const answerMetrics = ctx.measureText(answerText);
        const answerBgHeight = 22;
        
        ctx.fillStyle = 'white';
        const answerBgWidth = answerMetrics.width + 2 * bgPadding;
        const answerBgX = textX - answerBgWidth / 2;
        const answerBgY = textY - answerBgHeight / 2;
        ctx.fillRect(answerBgX, answerBgY, answerBgWidth, answerBgHeight);
        
        ctx.fillStyle = '#000000';
        ctx.fillText(answerText, textX, textY);
        
        if (label.explanation) {
          ctx.font = '12px Arial';
          const explText = stripHtmlTags(label.explanation);
          const explMetrics = ctx.measureText(explText);
          const explHeight = 16;
          const explY = textY + answerBgHeight / 2 + explHeight / 2 + 4;
          
          const explBgWidth = explMetrics.width + 2 * bgPadding;
          const explBgX = textX - explBgWidth / 2;
          const explBgY = explY - explHeight / 2;
          ctx.fillStyle = 'white';
          ctx.fillRect(explBgX, explBgY, explBgWidth, explHeight);
          
          ctx.fillStyle = '#333333';
          ctx.fillText(explText, textX, explY);
        }
      });
    }, [revealedIndices, baseImageLoaded, question.labels]);
    
    return (
      <Box sx={{ textAlign: "center", mt: 2 }}>
        <canvas ref={canvasRef} style={{ maxWidth: "100%", border: "2px solid #e0e0e0", borderRadius: "12px", display: "block", margin: "0 auto", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }} />
        <Typography variant="body2" sx={{ mt: 2, color: "#666", fontStyle: "italic" }}>
          {renderContent(question.statement || "")}
        </Typography>
        {!baseImageLoaded && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <CircularProgress size={24} />
            <Typography variant="caption" sx={{ ml: 1 }}>Loading image...</Typography>
          </Box>
        )}
      </Box>
    );
  };

  return (
   <Box
  sx={{
    padding: 4,
    minHeight: "100vh",
    background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
    overflow: "hidden", // Add this line
  }}
>
<style>{listStyles}</style> 
      <Typography variant="h3" align="center" sx={{ color: "#1e3a8a", fontWeight: "bold", mb: 1, textShadow: "2px 2px 4px rgba(0,0,0,0.1)" }}>
        {reviewMode ? "📚 Review Session" : `🎯 ${questionType.toUpperCase()} Practice`}
      </Typography>
      <Typography variant="subtitle1" align="center" sx={{ mb: 4, color: "#555", fontWeight: 500 }}>
        {folder?.name}
      </Typography>

      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
          <CircularProgress size={60} thickness={4} sx={{ color: "#1e8bafff" }} />
        </Box>
      )}

      {error && (
        <Box sx={{ textAlign: "center", mt: 5 }}>
          <Typography variant="h5" sx={{ color: "#d32f2f", mb: 2 }}>
            {error}
          </Typography>
          {error === "🎉 No due cards. You're all caught up!" && (
            <Button variant="contained" sx={{ mt: 2, background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)", px: 4, py: 1.5, borderRadius: 3 }} onClick={() => navigate("/flashpractice")}>
              Back to Practice
            </Button>
          )}
        </Box>
      )}

      {nextReviewAt && !isLoading && !error && questions.length === 0 && (
        <Box sx={{ textAlign: "center", mt: 5 }}>
          <Typography variant="h5" sx={{ mb: 2, color: "#2e7d32", fontWeight: "bold" }}>
            ⏰ Next review in <strong>{countdown || "calculating..."}</strong>
          </Typography>
          <Button variant="contained" onClick={() => navigate("/flashpractice")} sx={{ background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)", px: 4, py: 1.5, borderRadius: 3 }}>
            Back to Practice
          </Button>
        </Box>
      )}

      {allDone ? (
        <Box sx={{ textAlign: "center", mt: 5 }}>
          <Typography variant="h4" sx={{ mb: 3, color: "#2e7d32", fontWeight: "bold" }}>
            🎉 Congratulations!
          </Typography>
          <Typography variant="h6" sx={{ mb: 3, color: "#555" }}>
            {reviewMode ? "All due reviews finished!" : "Practice session complete!"}
          </Typography>
          <Button variant="contained" onClick={() => navigate("/flashpractice")} sx={{ background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)", px: 5, py: 2, fontSize: "1.1rem", borderRadius: 3 }}>
            Back to Home
          </Button>
        </Box>
      ) : (
        questions.length > 0 && (
<StyledCard key={currentQuestionIndex} isExiting={isExiting}>
            <CardHeader>
  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
    <QuestionBadge>
      Question {currentQuestionIndex + 1} of {questions.length}
    </QuestionBadge>
    <QuestionBadge sx={{
      backgroundColor: {
        mcq: "rgba(156, 39, 176, 0.3)",
        fib: "rgba(230, 81, 0, 0.3)",
        tf: "rgba(46, 125, 50, 0.3)",
        sub: "rgba(21, 101, 192, 0.3)",
        image: "rgba(0, 121, 107, 0.3)",
      }[questions[currentQuestionIndex]?.questionType] ?? "rgba(255,255,255,0.2)",
    }}>
      {questions[currentQuestionIndex]?.questionType?.toUpperCase()}
    </QuestionBadge>
  </Box>
  <IconButton onClick={handleClose} sx={{ color: "#fff", "&:hover": { backgroundColor: "rgba(255,255,255,0.2)" } }}>
    <CloseIcon />
  </IconButton>
</CardHeader>

            <StyledCardContent mode={mode} customColors={customColors}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: "600", mb: 3, lineHeight: 1.5, textAlign: "center" }}>
                  {renderContent((questions[currentQuestionIndex].statement || "").replace(/\[BLANK\]/g, "______"))}
                </Typography>

                {questions[currentQuestionIndex].file_url && questions[currentQuestionIndex].questionType !== "image" && (
                  <Box sx={{ textAlign: "center", mt: 2, mb: 3 }}>
                    <img src={getImageSrc(questions[currentQuestionIndex].file_url, apiUrl)} alt="Question" style={{ maxWidth: "100%", maxHeight: "300px", border: "2px solid #e0e0e0", borderRadius: "12px", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }} />
                  </Box>
                )}

                {renderOptions(questions[currentQuestionIndex])}

                {questions[currentQuestionIndex].questionType === "image" && revealedLabelIndices.length > 0 && renderAnswer(questions[currentQuestionIndex])}

                {showAnswer && questions[currentQuestionIndex].questionType !== "image" && renderAnswer(questions[currentQuestionIndex])}

                {!showAnswer && questions[currentQuestionIndex].questionType !== "image" && (
                  <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                    <ShowAnswerButton onClick={handleShowAnswer}>
                      👁️ Show Answer
                    </ShowAnswerButton>
                  </Box>
                )}
              </Box>

              {((questions[currentQuestionIndex].questionType === "image" && revealedLabelIndices.length > 0) || (questions[currentQuestionIndex].questionType !== "image" && showAnswer)) && (
                <Box sx={{ mt: 4, pt: 3, borderTop: "2px solid #e0e0e0" }}>
                  <Typography variant="subtitle2" align="center" sx={{ mb: 2, color: "#666", fontWeight: 600 }}>
                    How well did you know this?
                  </Typography>
                  <Box sx={{ display: "flex", justifyContent: "space-around", flexWrap: "wrap", gap: 1 }}>
                    <ImageButton onClick={() => handleNext("skip")} sx={{ color: mode === "dark" ? "#ddd" : mode === "custom" ? customColors.text : "#555" }}>
                      <Image src={skip} alt="skip" />
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>Skip</Typography>
                    </ImageButton>
                    <ImageButton onClick={() => handleNext("forgot")} sx={{ color: mode === "dark" ? "#ddd" : mode === "custom" ? customColors.text : "#555" }}>
                      <Image src={forgot} alt="forgot" />
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>Forgot</Typography>
                    </ImageButton>
                    <ImageButton onClick={() => handleNext("partially recalled")} sx={{ color: mode === "dark" ? "#ddd" : mode === "custom" ? customColors.text : "#555" }}>
                      <Image src={partially} alt="partially" />
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>Partial</Typography>
                    </ImageButton>
                    <ImageButton onClick={() => handleNext("recalled with effort")} sx={{ color: mode === "dark" ? "#ddd" : mode === "custom" ? customColors.text : "#555" }}>
                      <Image src={efforts} alt="efforts" />
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>With Effort</Typography>
                    </ImageButton>
                    <ImageButton onClick={() => handleNext("easily recalled")} sx={{ color: mode === "dark" ? "#ddd" : mode === "custom" ? customColors.text : "#555" }}>
                      <Image src={convenient} alt="convenient" />
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>Easy</Typography>
                    </ImageButton>
                  </Box>
                </Box>
              )}
            </StyledCardContent>
          </StyledCard>
        )
      )}

      <Snackbar open={srSnack.open} autoHideDuration={4500} onClose={() => setSrSnack((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={srSnack.severity} sx={{ boxShadow: 3, borderRadius: 2 }}>
          {srSnack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AllFlash;